import type { MarketingDialogContent } from '#shared/types/marketing'
import type { MarketingTrackCounter } from '~/stores/marketing'
import {
  MarketingSubjectType,
  MarketingTrackField,
} from '~/grpc/generated/content_pb.js'

const MCID_COOKIE = 'mcid'
const MCID_MAX_AGE = 60 * 60 * 24 * 365
const TRACK_FLUSH_INTERVAL_MS = 30000
const DIALOG_OPEN_DELAY_MS = 500

export function detectTrafficSource() {
  if (import.meta.server) return 'direct'

  const route = useRoute()
  const ref = document.referrer?.toLowerCase() || ''
  const ua = navigator.userAgent.toLowerCase()

  if (route.query.utm_source) {
    return `utm_${route.query.utm_source}`
  }

  if (ref.includes('t.me') || ref.includes('telegram.org') || ua.includes('telegram')) {
    return 'telegram_social'
  }

  if (ref.includes('vk.com')) return 'vk_social'
  if (ref.includes('yandex') || ref.includes('google')) return 'search_engine'

  return 'direct'
}

export default defineNuxtPlugin({
  name: 'app:marketing-initiator',
  dependsOn: ['app:grpc-instance', 'app:initialization', 'app:vue-final-model-setup'],

  setup() {
    const route = useRoute()
    const dialogStore = useDialogStore()
    const marketingStore = useMarketingStore()
    const userStore = useUserStore()

    const mcid = useCookie(MCID_COOKIE, {
      maxAge: MCID_MAX_AGE,
      sameSite: 'lax',
      path: '/',
    })
    if (!mcid.value) {
      mcid.value = crypto.randomUUID()
    }

    if (Object.keys(route.query).some(key => key.startsWith('utm_'))) {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)

      ;[...params.keys()].forEach((key) => {
        if (key.startsWith('utm_')) params.delete(key)
      })

      window.history.replaceState({}, '', `${url.pathname}${params.toString() ? '?' + params : ''}`)
    }

    const getSubject = () => {
      const userId = userStore.user?.id
      return userId
        ? { type: MarketingSubjectType.USER, id: String(userId) }
        : { type: MarketingSubjectType.GUEST, id: String(mcid.value) }
    }

    const dialogRegistry: Record<string, (content: MarketingDialogContent) => void> = {
      base_text: (content) => {
        dialogStore.modal?.marketingBaseText.patchOptions({ attrs: { ...content } })
        dialogStore.modal?.marketingBaseText.open()
      },
      base_image_text: (content) => {
        dialogStore.modal?.marketingBaseImageText.patchOptions({ attrs: { ...content } })
        dialogStore.modal?.marketingBaseImageText.open()
      },
    }

    const TRACK_FIELD_BY_COUNTER: Record<MarketingTrackCounter, MarketingTrackField> = {
      clicks: MarketingTrackField.CLICK,
      visits: MarketingTrackField.VISIT,
      exits: MarketingTrackField.EXIT,
    }

    const pendingTrack = new Map<string, { clicks: number, visits: number, exits: number }>()

    const addTrack = (campaignId: string, field: MarketingTrackCounter) => {
      const entry = pendingTrack.get(campaignId) || { clicks: 0, visits: 0, exits: 0 }
      entry[field] += 1
      pendingTrack.set(campaignId, entry)
    }

    const flushTrack = async () => {
      if (!pendingTrack.size) return

      const entries = [...pendingTrack.entries()]
      pendingTrack.clear()

      const subject = getSubject()
      for (const [campaignId, counters] of entries) {
        for (const counter of Object.keys(counters) as MarketingTrackCounter[]) {
          const delta = counters[counter]
          if (!delta) continue
          const { error } = await apiMarketingTrackEvent({
            subject,
            campaignId,
            field: TRACK_FIELD_BY_COUNTER[counter],
            delta,
          })
          if (error) {
            const entry = pendingTrack.get(campaignId) || { clicks: 0, visits: 0, exits: 0 }
            entry[counter] += delta
            pendingTrack.set(campaignId, entry)
          }
        }
      }
    }

    setInterval(() => void flushTrack(), TRACK_FLUSH_INTERVAL_MS)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) void flushTrack()
    })

    marketingStore.setTrackSink(addTrack)

    const mapTriggerOptions = (campaign: {
      triggerType?: unknown
      chance?: number
      visitCount?: number
      clickSelector?: string
      clickThreshold?: number
      focusOffLimit?: number
      intervalSec?: number
    }) => {
      const type = String(campaign.triggerType || '')

      let chance: number | `${number}visit` | `${number}fo` = 1
      if (type.endsWith('CHANCE')) {
        chance = campaign.chance ?? 1
      } else if (type.endsWith('VISIT')) {
        chance = `${campaign.visitCount ?? 1}visit`
      } else if (type.endsWith('FOCUS_OFF')) {
        chance = `${campaign.focusOffLimit ?? 1}fo`
      }

      return {
        interval: campaign.intervalSec ?? 86400,
        chance,
        ...(type.endsWith('CLICK_SELECTOR') && campaign.clickSelector
          ? {
              meta: {
                clickSelector: {
                  selector: campaign.clickSelector,
                  threshold: campaign.clickThreshold ?? 1,
                },
              },
            }
          : {}),
      }
    }

    const initCampaigns = async () => {
      const { data, error } = await apiMarketingGetActive({ subject: getSubject() })
      if (error || !data?.list) return

      for (const campaign of data.list) {
        const open = dialogRegistry[String(campaign.dialogKey)]
        if (!open) continue

        marketingStore.hydrateState(String(campaign.id), {
          clicks: Number(campaign.clicks) || 0,
          visits: Number(campaign.visits) || 0,
          exits: Number(campaign.exits) || 0,
          lastShownAt: campaign.lastShownAt ? Number(campaign.lastShownAt) : undefined,
        })

        marketingStore.tryInitialize(String(campaign.id), () => {
          setTimeout(() => {
            open({
              title: campaign.title,
              body: campaign.body,
              media: campaign.media || undefined,
              buttonLabel: campaign.buttonLabel,
              buttonLink: campaign.buttonLink,
              closable: Boolean(campaign.closable),
            })
            void flushTrack().then(() => apiMarketingMarkShown({ subject: getSubject(), campaignId: String(campaign.id) }))
          }, DIALOG_OPEN_DELAY_MS)
        }, mapTriggerOptions(campaign))
      }
    }

    void initCampaigns()

    watch(() => userStore.user?.id, async (id, previousId) => {
      if (id && !previousId && mcid.value) {
        await apiMarketingMergeGuestState({ guestClientId: mcid.value })
        await initCampaigns()
      }
    })
  },
})
